import {PyramidCategory} from 'src/app/components/categories-pyramid-display/categories-pyramid-display.component';
import {
  TeamHealthScoreVm,
  TeamIndicatorRowVm,
  TeamMemberVm,
  TeamReportHeaderVm
} from '../models/team-dynamic-team.vm';
import {ColorMap, getScoreColor, getScoreGradient} from './team-dynamic.mapper';

const INITIALS_COLORS = ['#8EC8E8', '#2551BF', '#3D9B6E', '#F5A623', '#6B7CFF'];

export function mapTeamReportHeader(report: any, members: any, baseUrl: string | undefined,
                                    fallbackTeamName: string): TeamReportHeaderVm {
  const list = Array.isArray(members) ? members : [];
  const mapped = list.map((member: any) => mapTeamMember(member, baseUrl));

  return {
    teamName: (report?.current?.team?.name ?? fallbackTeamName ?? '').trim(),
    leader: mapped.find((member: TeamMemberVm) => member.isLeader) ?? null,
    members: mapped.filter((member: TeamMemberVm) => !member.isLeader),
    membersCount: mapped.length
  };
}

export function mapTeamMember(member: any, baseUrl: string | undefined): TeamMemberVm {
  const user = member?.user ?? member ?? {};
  const name = getMemberName(user);

  return {
    uuid: member?.uuid ?? user?.uuid ?? '',
    name,
    initials: getMemberInitials(user),
    avatarUrl: user?.avatar?.path && baseUrl ? baseUrl + user.avatar.path : null,
    initialsColor: getInitialsColor(name || user?.email || ''),
    roleLabel: user?.teamRole?.label ?? '',
    isLeader: user?.teamRole?.name === 'team-admin'
  };
}

export function mapTeamHealthScore(report: any, intervals: any[], colors: ColorMap): TeamHealthScoreVm | null {
  const current = report?.current;
  if (!current) {
    return null;
  }
  const score = current.score ?? 0;
  const previousScore = report?.previous?.score;
  const hasPrevious = previousScore !== undefined && previousScore !== null;
  const workspaceScore = report?.workspaceScore;

  return {
    score,
    color: getScoreColor(score, intervals, colors),
    gradientColor: getScoreGradient(score, intervals, colors),
    trend: hasPrevious ? getTrend(score, previousScore) : null,
    diff: hasPrevious ? score - previousScore : null,
    workspaceScore: workspaceScore === undefined || workspaceScore === null ? null : workspaceScore,
    responseRate: current.responseRate ?? 0
  };
}

export function mapTeamIndicatorRows(categories: PyramidCategory[]): TeamIndicatorRowVm[] {
  return categories.map(category => ({
    uuid: category.uuid,
    title: category.title,
    icon: category.icon,
    color: category.color,
    score: category.score,
    trend: category.trend
  }));
}

function getMemberName(user: any): string {
  const fullName = ((user?.detail?.firstname ?? '') + ' ' + (user?.detail?.lastname ?? '')).trim();
  return user?.detail?.displayName || fullName || user?.email || '';
}

function getMemberInitials(user: any): string {
  const initials = ((user?.detail?.firstname ?? '').charAt(0) + (user?.detail?.lastname ?? '').charAt(0))
    .toUpperCase();
  return initials || (user?.email ?? '').slice(0, 2).toUpperCase();
}

function getInitialsColor(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }
  return INITIALS_COLORS[Math.abs(hash) % INITIALS_COLORS.length];
}

function getTrend(score: number, previousScore: number): 'up' | 'down' | null {
  return score === previousScore ? null : (score > previousScore ? 'up' : 'down');
}
